/**
 * Client-side access gate.
 *
 * On first visit, renders a blocking frosted overlay over the whole app: the
 * system logo above a blurred backdrop and a single text input — no labels,
 * no prompt. The app (`#app`) is made `inert` while the gate is up, so nothing
 * behind the blur can be focused, tabbed to, or interacted with until the
 * correct access phrase is entered. The unlock is remembered per-device.
 *
 * The access phrase is NOT stored in the bundle: only a salted SHA-256 digest
 * of it is (`ACCESS_HASH`). Reading the source therefore does not reveal the
 * password. Hashing uses a self-contained pure-JS SHA-256 — fully offline, no
 * network, and (unlike Web Crypto's `crypto.subtle`) no secure-context
 * requirement, so it also works in the Electron `file://` build.
 *
 * IMPORTANT — this is still a *soft* gate, not authentication. Garden Manager
 * is a static, backend-less bundle, so the check runs in the browser and the
 * gate itself can be bypassed by editing/disabling the page JS. Hashing hides
 * the password *value*; it does not make the barrier unbreakable. Real,
 * unbypassable protection would require server-/hosting-level auth — which the
 * user has explicitly declined in favour of staying fully offline.
 */

// Kept deliberately OUTSIDE config-store's managed key set so it survives a
// factory reset / full-backup import (the user shouldn't be re-gated by those).
const UNLOCK_KEY = 'shavzak_access_unlocked';

// Salt + SHA-256 digest of `SALT + normalize(access phrase)`. The salt defeats
// trivial "look the hash up online" attempts; the plaintext lives nowhere in
// the source.
const SALT = 'shavzak360::v1::';
const ACCESS_HASH = '01c459ffc0da55f743553f7c1f7b635f5f8c93c3e2b7f542a30f7b17a4fc64d0';

// --- Pure-JS SHA-256 (FIPS 180-4) ------------------------------------------
// Self-contained so it needs neither a network nor a secure context.

const SHA256_K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
  0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
  0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
  0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
]);

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n));
}

/** SHA-256 of a UTF-8 string → lowercase hex. */
function sha256Hex(message: string): string {
  const bytes = new TextEncoder().encode(message);
  const len = bytes.length;
  const totalLen = (((len + 8) >> 6) + 1) << 6; // padded to a multiple of 64 bytes
  const padded = new Uint8Array(totalLen);
  padded.set(bytes);
  padded[len] = 0x80;
  const dv = new DataView(padded.buffer);
  const bitLen = len * 8;
  dv.setUint32(totalLen - 8, Math.floor(bitLen / 0x100000000));
  dv.setUint32(totalLen - 4, bitLen >>> 0);

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;
  const w = new Uint32Array(64);

  for (let off = 0; off < totalLen; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    for (let i = 0; i < 64; i++) {
      const big1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + big1 + ch + SHA256_K[i] + w[i]) | 0;
      const big0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (big0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  const hex = (x: number): string => (x >>> 0).toString(16).padStart(8, '0');
  return hex(h0) + hex(h1) + hex(h2) + hex(h3) + hex(h4) + hex(h5) + hex(h6) + hex(h7);
}

// ---------------------------------------------------------------------------

/** Trim ends and collapse internal whitespace so spacing slips don't reject. */
function normalize(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

/** True when the typed value hashes to the stored access digest. */
function matchesAccessPhrase(raw: string): boolean {
  return sha256Hex(SALT + normalize(raw)) === ACCESS_HASH;
}

export function isUnlocked(): boolean {
  try {
    return localStorage.getItem(UNLOCK_KEY) === '1';
  } catch {
    // Storage blocked (private mode, etc.) — gate every load rather than throw.
    return false;
  }
}

export function showAuthGate(): void {
  // Idempotent — never stack two gates.
  if (document.getElementById('gm-auth-gate')) return;

  const app = document.getElementById('app');
  app?.setAttribute('inert', '');
  document.body.classList.add('gm-auth-locked');

  const overlay = document.createElement('div');
  overlay.id = 'gm-auth-gate';
  overlay.className = 'gm-auth-gate';

  const logo = document.createElement('img');
  logo.className = 'gm-auth-logo';
  logo.src = './icon-512.png';
  logo.alt = '';
  logo.setAttribute('aria-hidden', 'true');
  logo.draggable = false;

  const input = document.createElement('input');
  input.type = 'password';
  input.className = 'gm-auth-input';
  input.dir = 'rtl';
  input.autocomplete = 'off';
  input.setAttribute('autocapitalize', 'off');
  input.setAttribute('autocorrect', 'off');
  input.spellcheck = false;
  // a11y affordance only — not shown, keeps the screen text-free as requested.
  input.setAttribute('aria-label', 'כניסה');

  overlay.appendChild(logo);
  overlay.appendChild(input);
  document.body.appendChild(overlay);

  const onFocusIn = (e: FocusEvent): void => {
    if (!document.body.contains(overlay)) return;
    if (e.target !== input) {
      e.stopPropagation();
      input.focus();
    }
  };

  const unlock = (): void => {
    try {
      localStorage.setItem(UNLOCK_KEY, '1');
    } catch {
      // Non-fatal: the user simply re-enters next load.
    }
    document.removeEventListener('focusin', onFocusIn, true);
    document.body.classList.remove('gm-auth-locked');
    app?.removeAttribute('inert');
    overlay.classList.add('gm-auth-gate--exit');
    window.setTimeout(() => overlay.remove(), 450);
  };

  const reject = (): void => {
    input.classList.remove('gm-auth-shake');
    // Force reflow so the shake animation restarts on a repeated wrong entry.
    void input.offsetWidth;
    input.classList.add('gm-auth-shake');
    input.value = '';
  };

  // Auto-unlock the instant the phrase matches — no Enter / button needed,
  // keeping the screen instruction-free.
  input.addEventListener('input', () => {
    if (matchesAccessPhrase(input.value)) unlock();
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (matchesAccessPhrase(input.value)) unlock();
      else reject();
    }
  });

  // Trap focus on the input for as long as the gate is up.
  document.addEventListener('focusin', onFocusIn, true);
  input.focus();
  window.setTimeout(() => input.focus(), 50);
}
